import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaIcon } from '../FaIcon';

// Kinds reconocidos. Quien renderiza el componente pasa el kind + ctaContext.
// Mantener sincronizado con EMPTY_STATE_CONFIGS abajo.
export const EMPTY_STATE_KINDS = Object.freeze({
    NO_AGENCY_CLIENTS: 'no_agency_clients',
    NO_BUSINESS_UNITS: 'no_business_units',
    NO_AD_ACCOUNTS: 'no_ad_accounts',
    ACCOUNT_NO_ACCESS: 'account_no_access',
    DATA_PREPARING: 'data_preparing',
    NO_RESULTS: 'no_results',
    GENERIC_ERROR: 'generic_error',
});

// Configuración por kind: icono, tono, claves de i18n, builder de href de CTA.
// El href se construye con ctaContext.agencyClientId si está disponible; si no,
// la CTA se oculta (el usuario no tiene cliente seleccionado y el destino no aplica).
const EMPTY_STATE_CONFIGS = {
    [EMPTY_STATE_KINDS.NO_AGENCY_CLIENTS]: {
        tone: 'info',
        icon: 'users-slash',
        titleKey: 'data_explorer.empty_state.no_agency_clients.title',
        descKey: 'data_explorer.empty_state.no_agency_clients.description',
        ctaLabelKey: 'data_explorer.empty_state.no_agency_clients.cta',
        buildHref: () => '/clients',
    },
    [EMPTY_STATE_KINDS.NO_BUSINESS_UNITS]: {
        tone: 'info',
        icon: 'bullseye',
        titleKey: 'data_explorer.empty_state.no_business_units.title',
        descKey: 'data_explorer.empty_state.no_business_units.description',
        ctaLabelKey: 'data_explorer.empty_state.no_business_units.cta',
        buildHref: (ctx) =>
            ctx.agencyClientId ? `/client/${ctx.agencyClientId}/business-units` : null,
    },
    [EMPTY_STATE_KINDS.NO_AD_ACCOUNTS]: {
        tone: 'info',
        icon: 'plug-circle-plus',
        titleKey: 'data_explorer.empty_state.no_ad_accounts.title',
        descKey: 'data_explorer.empty_state.no_ad_accounts.description',
        ctaLabelKey: 'data_explorer.empty_state.no_ad_accounts.cta',
        buildHref: (ctx) =>
            ctx.agencyClientId ? `/client/${ctx.agencyClientId}/conections` : null,
    },
    [EMPTY_STATE_KINDS.ACCOUNT_NO_ACCESS]: {
        tone: 'warning',
        // `unlink` (FA5) y no `link-slash` (FA6): el nombre nuevo NO está en
        // `init_icons_library.js` y no dibuja nada, sin error ni warning visible. No se notaba
        // porque este kind era inalcanzable hasta la card `4By6KR9g`.
        // ⚠️ Los otros tres siguen rotos por lo mismo, y quedan fuera de esta card:
        // `users-slash`, `plug-circle-plus` y `magnifying-glass` tampoco están registrados.
        icon: 'unlink',
        titleKey: 'data_explorer.empty_state.account_no_access.title',
        descKey: 'data_explorer.empty_state.account_no_access.description',
        ctaLabelKey: 'data_explorer.empty_state.account_no_access.cta',
        buildHref: (ctx) =>
            ctx.agencyClientId ? `/client/${ctx.agencyClientId}/conections` : null,
    },
    [EMPTY_STATE_KINDS.DATA_PREPARING]: {
        tone: 'warning',
        icon: 'clock',
        titleKey: 'data_explorer.empty_state.data_preparing.title',
        descKey: 'data_explorer.empty_state.data_preparing.description',
        // Sin CTA href: se ofrece botón Reintentar via onRetry.
        ctaLabelKey: null,
        buildHref: () => null,
    },
    [EMPTY_STATE_KINDS.NO_RESULTS]: {
        tone: 'neutral',
        icon: 'magnifying-glass',
        titleKey: 'data_explorer.empty_state.no_results.title',
        descKey: 'data_explorer.empty_state.no_results.description',
        ctaLabelKey: null,
        buildHref: () => null,
    },
    [EMPTY_STATE_KINDS.GENERIC_ERROR]: {
        tone: 'warning',
        icon: 'triangle-exclamation',
        titleKey: 'data_explorer.empty_state.generic_error.title',
        descKey: 'data_explorer.empty_state.generic_error.description',
        ctaLabelKey: null,
        buildHref: () => null,
    },
};

/**
 * @param description texto del backend que REEMPLAZA al copy fijo del kind. Sólo lo pasa quien ya
 *        decidió que ese mensaje se puede mostrar (ver `typedQueryError.js`): el componente no
 *        vuelve a evaluarlo. Ausente = comportamiento de siempre.
 */
export function DataExplorerEmptyState({ kind, ctaContext = {}, onRetry, description }) {
    const { t } = useTranslation();
    const config = EMPTY_STATE_CONFIGS[kind];

    if (!config) return null;

    const href = config.buildHref ? config.buildHref(ctaContext) : null;
    const showCtaLink = !!(config.ctaLabelKey && href);
    const showRetry = typeof onRetry === 'function';

    return (
        <div className={`data-explorer-empty-state -${config.tone}`}>
            <div className="data-explorer-empty-state__icon-circle">
                <FaIcon iconName={config.icon} />
            </div>
            <h3 className="data-explorer-empty-state__title">{t(config.titleKey)}</h3>
            <p className="data-explorer-empty-state__description">
                {description || t(config.descKey)}
            </p>
            {(showCtaLink || showRetry) && (
                <div className="data-explorer-empty-state__actions">
                    {showCtaLink && (
                        <a href={href} className="btn -alt">
                            {t(config.ctaLabelKey)}
                        </a>
                    )}
                    {showRetry && (
                        <button
                            type="button"
                            className={showCtaLink ? 'btn -alt -negative' : 'btn -alt'}
                            onClick={onRetry}
                        >
                            {t('data_explorer.empty_state.retry')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
